/**
 * Button Component
 *
 * Purpose: A versatile button component with multiple variants and styling options.
 *
 * Features:
 * - Multiple visual variants (default, destructive, outline, secondary, ghost, link, etc.)
 * - Size variants (default, small, large, icon)
 * - Hover effects and animations
 * - Accessibility support
 * - Custom styling integration with design system
 * - Support for asChild pattern via Radix Slot
 *
 * Parameters:
 * @param variant (optional) - Button style variant: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "security" | "success" | "gradient" | "glass"
 * @param size (optional) - Button size: "default" | "sm" | "lg" | "icon"
 * @param asChild (optional) - Render as child element instead of button
 * @param className (optional) - Additional CSS classes
 * @param children (mandatory) - Button content
 *
 * Variant Descriptions:
 * - default: Primary button with brand colors
 * - destructive: Danger/delete actions
 * - outline: Bordered button with transparent background
 * - secondary: Secondary actions with muted colors
 * - ghost: Minimal button without background
 * - link: Text-only button with underline
 * - security: Special styling for security-related actions
 * - success: Success/confirmation actions
 * - gradient: Premium button with gradient background
 * - glass: Glassmorphism effect button
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95',
  {
    variants: {
      variant: {
        default:
          'btn-primary bg-primary text-primary-foreground hover:bg-primary/90 shadow-soft hover:shadow-medium hover:translate-y-[-1px] transition-all duration-300',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-soft hover:shadow-medium',
        outline:
          'border border-input bg-background hover:bg-gradient-to-r from-primary to-primary-glow hover:text-accent-foreground shadow-soft hover:shadow-medium hover:border-primary/20',
        secondary:
          'btn-secondary bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-soft hover:shadow-medium',
        ghost:
          'hover:bg-accent hover:text-accent-foreground transition-all duration-200',
        link: 'text-primary underline-offset-4 hover:underline transition-colors duration-200',
        security:
          'bg-security text-security-foreground hover:bg-security/90 shadow-colored hover:shadow-glow',
        success:
          'bg-success text-success-foreground hover:bg-success/90 shadow-soft hover:shadow-medium',
        gradient:
          'bg-gradient-to-r from-primary to-primary-glow text-primary-foreground hover:shadow-glow transition-all duration-300 hover:scale-105',
        glass:
          'bg-gradient-glass backdrop-blur-sm border border-white/20 text-foreground hover:bg-gradient-glass/80 shadow-glass',
        secure:
          'bg-gradient-to-r from-primary to-primary-glow hover:from-primary/90 hover:to-primary-glow/90 shadow-lg hover:shadow-xl border border-primary/20 relative overflow-hidden group text-primary-foreground',
        encrypted:
          'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 relative after:absolute after:top-1 after:right-1 after:w-2 after:h-2 after:bg-green-500 after:rounded-full after:animate-pulse',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-md px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants, type ButtonProps };
